import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { arrayMove } from '@dnd-kit/sortable'

import { useFetch } from '@reveni-io/reveni-ui'

import configurationServices from '@/configuration/services/services'
import routes from '@/Router/routes.constants'

import { pageSize } from './constants'
import { transformRequestReorderVisibilityCriteria, transformResponseVisibilityCriteria } from './transformers'

const detailRoute = routes.configurationPage.orderTrackingPage.visibilityCriterionDetail

const useVisibilityCriteria = ({ storeId, setToastInfo }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const {
    data: visibilityCriteriaData,
    fetchData: fetchVisibilityCriteria,
    loading: gettingVisibilityCriteria,
  } = useFetch(configurationServices.getVisibilityCriteria, {
    doInitialRequest: false,
    transformResponse: transformResponseVisibilityCriteria,
  })

  const { fetchData: reorderVisibilityCriteriaRequest, loading: reorderingVisibilityCriteria } = useFetch(
    configurationServices.reorderVisibilityCriteria,
    { doInitialRequest: false, transformRequest: transformRequestReorderVisibilityCriteria }
  )

  const { fetchData: deleteVisibilityCriterionRequest, loading: deletingVisibilityCriterion } = useFetch(
    configurationServices.deleteVisibilityCriterion,
    { doInitialRequest: false }
  )

  const [visibilityCriteria, setVisibilityCriteria] = useState([])
  const [hasChanges, setHasChanges] = useState(false)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [criterionToDelete, setCriterionToDelete] = useState(null)
  const pageRef = useRef(1)

  useEffect(() => {
    setPage(1)
    pageRef.current = 1
  }, [search])

  useEffect(() => {
    pageRef.current = page
    fetchVisibilityCriteria({ storeId, search, page, pageSize })
  }, [storeId, search, page, fetchVisibilityCriteria])

  const setInitValues = useCallback(() => {
    if (visibilityCriteriaData) {
      if (pageRef.current === 1) {
        setVisibilityCriteria(visibilityCriteriaData.results)
      } else {
        setVisibilityCriteria(prev => [...prev, ...visibilityCriteriaData.results])
      }
    }

    setHasChanges(false)
  }, [visibilityCriteriaData])

  useEffect(() => {
    setInitValues()
  }, [setInitValues])

  const reorderCriteria = event => {
    const { active, over } = event

    if (!over || active.id === over.id) return

    setVisibilityCriteria(prev => {
      const oldIndex = prev.findIndex(criterion => criterion.id === active.id)
      const newIndex = prev.findIndex(criterion => criterion.id === over.id)

      return arrayMove(prev, oldIndex, newIndex)
    })
    setHasChanges(true)
  }

  const refetchFromStart = useCallback(() => {
    setPage(1)
    pageRef.current = 1
    fetchVisibilityCriteria({ storeId, search, page: 1, pageSize })
  }, [storeId, search, fetchVisibilityCriteria])

  const loadMore = () => {
    setPage(prev => prev + 1)
  }

  const onSave = async () => {
    try {
      await reorderVisibilityCriteriaRequest({ storeId, criteria: visibilityCriteria })
      setToastInfo({ show: true, message: t('Cambios guardados'), type: 'success' })
      refetchFromStart()
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const revertChanges = () => {
    setInitValues()
  }

  const confirmDeleteCriterion = async () => {
    try {
      await deleteVisibilityCriterionRequest({ storeId, criterionId: criterionToDelete.id })
      setToastInfo({ show: true, message: t('Eliminado correctamente'), type: 'success' })
      setCriterionToDelete(null)
      refetchFromStart()
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const cancelDeleteCriterion = () => {
    setCriterionToDelete(null)
  }

  const navigateToDetail = criterion => {
    navigate(detailRoute.replace(':id', criterion.id), {
      state: { childPageTitle: criterion.name, selectedCriterion: criterion },
    })
  }

  const navigateToDuplicate = criterion => {
    navigate(detailRoute.replace(':id', `duplicate-${criterion.id}`), {
      state: { selectedCriterion: criterion },
    })
  }

  const navigateToCreate = () => {
    navigate(detailRoute.replace(':id', 'new'))
  }

  return {
    visibilityCriteria,
    gettingVisibilityCriteria,
    reorderingVisibilityCriteria,
    deletingVisibilityCriterion,
    hasChanges,
    search,
    setSearch,
    loadMore,
    hasNextPage: !!visibilityCriteriaData?.next,
    totalCount: visibilityCriteriaData?.count || 0,
    reorderCriteria,
    onSave,
    revertChanges,
    criterionToDelete,
    setCriterionToDelete,
    confirmDeleteCriterion,
    cancelDeleteCriterion,
    navigateToDetail,
    navigateToDuplicate,
    navigateToCreate,
  }
}

export default useVisibilityCriteria
